import {Suspense, useEffect} from 'react'
import {RouterProvider} from 'react-router-dom'
import {SpinLoading, WaterMark} from 'antd-mobile'
import {createRoot} from 'react-dom/client'
import ErrorBoundary from '@/components/ErrorBoundary'
import {DictProvider, setGlobalDictContext, useDict} from '@/utils/dict'
import {AuthProvider, useAuth} from '@/utils/auth'
import './index.less'
import './styles/mobile.less'
import router from './router'

/**
 * Eruda 调试工具初始化组件
 * 开发环境自动启用；生产环境仅通过 localStorage key 'eruda' 启用（由个人信息页面 admin 开关控制）
 */
function ErudaInitializer() {
    useEffect(() => {
        const shouldEnable =
            import.meta.env.DEV ||
            localStorage.getItem('eruda') === '1'

        if (!shouldEnable) return

        // 检查是否已经初始化过，避免重复初始化
        if ((window as unknown as Record<string, unknown>).__eruda_initialized__) {
            return
        }

        import('eruda').then((eruda) => {
            eruda.default.init()
            ;(window as unknown as Record<string, unknown>).__eruda_initialized__ = true
        })
    }, [])

    return null
}

/**
 * 字典初始化组件
 * 将字典 Context 设置到全局
 */
function DictInitializer() {
    const dictContext = useDict()

    useEffect(() => {
        setGlobalDictContext(dictContext)
    }, [dictContext])

    return null
}

/**
 * 水印组件
 * 使用用户名作为水印内容
 */
function AppWaterMark() {
    const {userInfo} = useAuth()

    if (!userInfo) {
        return null
    }

    return (
        <WaterMark
            content={userInfo.username || '郴州小牛电动'}
            gapX={120}
            gapY={80}
            fontSize={14}
            fontColor="rgba(0, 0, 0, 0.08)"
            rotate={-15}
        />
    )
}

// 全局加载组件
const GlobalLoading = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
        }}
    >
        <SpinLoading color="primary"/>
    </div>
)

createRoot(document.getElementById('root')!).render(
    <ErrorBoundary>
        <AuthProvider>
            <DictProvider>
                <DictInitializer/>
                <ErudaInitializer/>
                <AppWaterMark/>
                <Suspense fallback={<GlobalLoading/>}>
                    <RouterProvider router={router}/>
                </Suspense>
            </DictProvider>
        </AuthProvider>
    </ErrorBoundary>,
)
